import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { communicationsApi } from '../api/communications';
import { documentsApi } from '../api/documents';
import type {
  Communication,
  CommunicationDirection,
  CommunicationKind,
  DocumentItem,
} from '../types';

const INPUT_CLASS =
  'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

const KIND_LABELS: Record<CommunicationKind, string> = {
  call: 'Anruf',
  email: 'E-Mail',
  letter: 'Brief',
};

const KIND_OPTIONS: CommunicationKind[] = ['call', 'email', 'letter'];

const CONTACT_LABELS: Record<CommunicationKind, { name: string; address: string; addressPh: string }> = {
  call: { name: 'Name', address: 'Telefonnummer', addressPh: '+49 ...' },
  email: { name: 'Name', address: 'E-Mail-Adresse', addressPh: 'name@beispiel.de' },
  letter: { name: 'Name', address: 'Postadresse', addressPh: 'Strasse, PLZ Ort' },
};

interface CommunicationEntryModalProps {
  entityType: string;
  entityId: number;
  /** When provided: edit mode; otherwise create. */
  existing?: Communication | null;
  /** Pre-select kind/direction on open (create mode only). */
  initialKind?: CommunicationKind;
  initialDirection?: CommunicationDirection;
  onClose: () => void;
  onSaved?: (comm: Communication) => void;
}

function toDateInputValue(s: string | null | undefined): string {
  if (!s) return new Date().toISOString().slice(0, 10);
  return s.slice(0, 10);
}

export default function CommunicationEntryModal({
  entityType,
  entityId,
  existing = null,
  initialKind = 'letter',
  initialDirection = 'outgoing',
  onClose,
  onSaved,
}: CommunicationEntryModalProps) {
  const queryClient = useQueryClient();
  const isEdit = !!existing;

  const [kind, setKind] = useState<CommunicationKind>(existing?.kind ?? initialKind);
  const [direction, setDirection] = useState<CommunicationDirection>(
    existing?.direction ?? initialDirection,
  );
  const [occurredAt, setOccurredAt] = useState<string>(toDateInputValue(existing?.occurred_at));
  const [contactName, setContactName] = useState<string>(existing?.contact_name ?? '');
  const [contactAddress, setContactAddress] = useState<string>(existing?.contact_address ?? '');
  const [subject, setSubject] = useState<string>(existing?.subject ?? '');
  const [bodyText, setBodyText] = useState<string>(existing?.body_text ?? '');
  const [documentId, setDocumentId] = useState<number | null>(existing?.document_id ?? null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Document list for the "Vorhandenes Dokument anhängen" dropdown
  const { data: documents = [] } = useQuery({
    queryKey: ['documents', entityType, entityId],
    queryFn: () => documentsApi.getDocuments(entityType, entityId),
  });

  // When entering edit mode for a letter draft, the body_text is owned by the wizard.
  const bodyDisabled = isEdit && existing?.kind === 'letter' && existing.status === 'draft';

  const uploadMutation = useMutation({
    mutationFn: (file: File) => documentsApi.uploadDocument(entityType, entityId, file),
    onSuccess: (uploaded: DocumentItem) => {
      queryClient.invalidateQueries({ queryKey: ['documents', entityType, entityId] });
      setDocumentId(uploaded.id);
    },
  });

  const createMutation = useMutation({
    mutationFn: () =>
      communicationsApi.createCommunication(entityType, entityId, {
        kind,
        direction,
        occurred_at: new Date(occurredAt).toISOString(),
        contact_name: contactName || null,
        contact_address: contactAddress || null,
        subject: subject || null,
        body_text: bodyText || null,
        document_id: documentId,
      }),
    onSuccess: (comm) => {
      queryClient.invalidateQueries({ queryKey: ['communications', entityType, entityId] });
      onSaved?.(comm);
      onClose();
    },
    onError: () => setError('Speichern fehlgeschlagen.'),
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      communicationsApi.updateCommunication(existing!.id, {
        occurred_at: new Date(occurredAt).toISOString(),
        contact_name: contactName || null,
        contact_address: contactAddress || null,
        subject: subject || null,
        body_text: bodyDisabled ? undefined : (bodyText || null),
        document_id: documentId,
      }),
    onSuccess: (comm) => {
      queryClient.invalidateQueries({ queryKey: ['communications', entityType, entityId] });
      onSaved?.(comm);
      onClose();
    },
    onError: () => setError('Speichern fehlgeschlagen.'),
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadMutation.mutate(file);
      e.target.value = '';
    }
  };

  const handleSubmit = () => {
    setError(null);
    if (isEdit) updateMutation.mutate();
    else createMutation.mutate();
  };

  // Keep contact-address placeholder in sync with kind
  useEffect(() => {
    // No-op effect just for stable hook order with kind state
  }, [kind]);

  const labels = CONTACT_LABELS[kind];
  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-full flex flex-col">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h3 className="text-lg font-medium">
            {isEdit ? 'Kommunikation bearbeiten' : 'Neuer Kommunikations-Eintrag'}
          </h3>
          <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700">
            Schließen
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto space-y-4">
          {/* Kind + Direction */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Art</label>
              <div className="flex rounded-md border border-gray-300 overflow-hidden">
                {KIND_OPTIONS.map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => !isEdit && setKind(k)}
                    disabled={isEdit}
                    className={`flex-1 px-3 py-1.5 text-sm transition-colors ${
                      kind === k
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50'
                    }`}
                  >
                    {KIND_LABELS[k]}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Richtung</label>
              <div className="flex rounded-md border border-gray-300 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setDirection('incoming')}
                  className={`flex-1 px-3 py-1.5 text-sm transition-colors ${
                    direction === 'incoming'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Eingehend
                </button>
                <button
                  type="button"
                  onClick={() => setDirection('outgoing')}
                  className={`flex-1 px-3 py-1.5 text-sm transition-colors ${
                    direction === 'outgoing'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Ausgehend
                </button>
              </div>
            </div>
          </div>

          {/* Date */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Zeitpunkt</label>
            <input
              type="date"
              value={occurredAt}
              onChange={(e) => setOccurredAt(e.target.value)}
              className={INPUT_CLASS}
            />
          </div>

          {/* Contact */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{labels.name}</label>
              <input
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                className={INPUT_CLASS}
                placeholder="Optional"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{labels.address}</label>
              <input
                value={contactAddress}
                onChange={(e) => setContactAddress(e.target.value)}
                className={INPUT_CLASS}
                placeholder={labels.addressPh}
              />
            </div>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Betreff</label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className={INPUT_CLASS}
              placeholder="Kurze Zusammenfassung"
            />
          </div>

          {/* Body */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {kind === 'call' ? 'Notiz' : kind === 'email' ? 'E-Mail-Inhalt' : 'Inhalt / Notiz'}
            </label>
            <textarea
              value={bodyText}
              onChange={(e) => setBodyText(e.target.value)}
              className={INPUT_CLASS}
              rows={4}
              disabled={bodyDisabled}
              placeholder={bodyDisabled ? 'Inhalt wird vom Brief-Wizard verwaltet.' : ''}
            />
          </div>

          {/* Attachment */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Anhang (optional)</label>
            <div className="flex gap-2 items-center">
              <select
                value={documentId ?? ''}
                onChange={(e) => setDocumentId(e.target.value ? Number(e.target.value) : null)}
                className={`${INPUT_CLASS} flex-1`}
              >
                <option value="">— Kein Anhang —</option>
                {documents.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.filename}
                  </option>
                ))}
              </select>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileSelect}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadMutation.isPending}
                className="text-sm text-blue-600 hover:text-blue-800 whitespace-nowrap disabled:opacity-50"
              >
                {uploadMutation.isPending ? 'Wird hochgeladen…' : '+ Hochladen'}
              </button>
            </div>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <div className="px-6 py-3 border-t flex items-center justify-end gap-3">
          <button onClick={onClose} className="text-sm text-gray-600 hover:text-gray-800">
            Abbrechen
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="bg-blue-600 text-white text-sm px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {isSubmitting ? 'Speichern…' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}
